import NsContent from 'components/common/ns-content/NsContent';

import { NsList } from 'components/common/ns-list/NsList';
import { NsAlert } from 'components/common/ns-alert/NsAlert';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsForm } from 'components/common/ns-form/NsForm';
import { Error } from 'components/common/error/Error';
import { sendSalesOrderThunk } from 'state/sales-order/sales-order-thunks';
import { useOrderSummary } from 'components/direct-sales/order-summary/hooks/useOrderSummary';
import { TermsAndConditions } from 'components/direct-sales/order-summary/components/TermsAndConditions';
import { SummaryContentList } from 'components/direct-sales/order-summary/components/SummaryContentList';
import { CreditOffer } from 'components/direct-sales/order-summary/components/CreditOffer';
import { Agreement } from 'components/direct-sales/order-summary/components/Agreement';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { AEM_PAGES, PARTNERS } from 'config/app-config';
import { useNavigation } from 'hooks/useNavigation';
import { useAemContent } from 'hooks/useAemContent';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  AbandonedCartUpdateStep,
  useAbandonedCartUpdate
} from 'hooks/useAbandonedCart/useAbandonedCartUpdate';
import NsInputterEvent from 'components/common/ns-inputter/NsInputterEvent';
import { patchAbandonedCartThunk } from 'state/abandoned-cart/abandoned-cart-thunks';

export const testId = 'orderSummaryTestId';
export const testIdVoicesaleAlert = 'testIdVoicesaleAlert';
export const testIdVoicesaleWinbackAlert = 'testIdVoicesaleWinbackAlert';
export const testIdTouAlertForAgent = 'testIdTouAlertForAgent';

export const OrderSummary = () => {
  const dispatch = useAppDispatch();
  const {
    setTermsValue,
    salesOrderPayload,
    errors,
    isLoading,
    tariffTermsPDF,
    isTOU
  } = useOrderSummary();
  const { eventTypes, next } = useNavigation();

  const isLoginEnabledJourney = useIsLoginEnabledJourney({
    exclude: [PARTNERS.NONDOMESTIC_TRANSFER]
  });

  const {
    isAbandonedCartAvailable,
    payload: abandonedCartPayload,
    encryptedQuoteId
  } = useAbandonedCartUpdate(AbandonedCartUpdateStep.CONFIRMATION);

  const handleChange = (event: NsInputterEvent) => {
    const target = event.target as JSX.IntrinsicElements['ns-inputter'];
    const value = target.value;
    setTermsValue(!!value);
  };

  const handleGoNext = async () => {
    try {
      await dispatch(sendSalesOrderThunk(salesOrderPayload));
      next({ eventType: eventTypes.NEXT });

      isAbandonedCartAvailable &&
        (await dispatch(patchAbandonedCartThunk(abandonedCartPayload, encryptedQuoteId)));
    } catch (e) {
      e && console.error(e);
    }
  };

  useAemContent(AEM_PAGES.CrossSellOffer);

  return (
    <NsPanel testId={testId}>
      <NsContent>
        <NsBackCta />
        <NsTitle title="Order Summary" />
        <SummaryContentList />
      </NsContent>
      <NsForm onSubmit={handleGoNext}>
        <CreditOffer />
        {isLoginEnabledJourney && isTOU && (
          <NsAlert
            heading="Read to the customer before accepting the quote"
            state="info"
            testId={testIdTouAlertForAgent}
          >
            <p>
              Before we proceed, I’m going to give you more information about
              half-hourly meter readings.
            </p>
            <NsList>
              {[
                `Half-hourly meter readings can help manage your energy usage, potentially cutting costs.`,
                `We may use half-hourly data to offer you tailored energy products and services`,
                `If you switch tariffs, we’ll still collect readings every half-hour. You can change your meter reading frequency at any time in your online account. However, you may no longer be eligible for this tariff.`
              ]}
            </NsList>
          </NsAlert>
        )}
        {isLoginEnabledJourney ? (
          <NsAlert
            heading="Read to the customer before accepting the quote"
            state="info"
            testId={testIdVoicesaleAlert}
          >
            <p>
              We will confirm everything in the next 7 days. You will receive a
              Welcome comm from which you can create your online account and see
              your documents and terms and conditions. You have 14 calendar days
              from tomorrow if you wish to change your mind. More information
              can be found at britishgas.co.uk/cooloff{' '}
            </p>
            <p>DD’s will be re-assessed throughout the year.</p>
          </NsAlert>
        ) : (
          <Agreement isTOU={isTOU} />
        )}

        <TermsAndConditions
          handleChange={handleChange}
          tariffTermsPDF={tariffTermsPDF || undefined}
          isTOU={isTOU}
        />

        <Error errors={errors} />
        <NsPrimaryCta isLoading={isLoading}>Complete order</NsPrimaryCta>
      </NsForm>
    </NsPanel>
  );
};

export default OrderSummary;
